import {
  LAYOUTS, LAYOUT_CYCLE, QUALITY_LADDER, type Angle, type Ceiling, type LayoutId, type Match,
  type MaxQuality,
} from '@multiview/shared';
import { api, type ScoreEventDto, type TelemetryReport } from '../api/client.ts';
import { maxFullQuality, maxTiles, platform } from '../platform.ts';
import { TilePlayer, type Quality, type TileState, type TileStats } from './TilePlayer.ts';
import { ExcitementDetector, type ExcitementEvent } from './excitement.ts';
import * as audioBus from './audioBus.ts';
import {
  effectiveCeiling, fullRendition, getMaxQuality, hintFor, onMaxQualityChange, setVariantHint,
  variantHint,
} from './quality.ts';
import { displayDescription, matchFrameRate } from './display.ts';

export interface TileSlot {
  angle: Angle;
  /** Grid area name, 'm' for the main pane. */
  slot: string;
  state: TileState;
  error: string | null;
  stats: TileStats | null;
}

/** Delay before downgrading the outgoing main pane. No rush, and it avoids
 *  two quality switches fighting over bandwidth during a fast cycle. */
const DOWNGRADE_DELAY_MS = 2500;

/**
 * How many off-screen angles to keep warm in the proxy.
 *
 * Deliberately one, not several: every warmed angle is one more concurrent
 * connection to the provider, and IPTV subscriptions cap those. Forward cycling
 * is the common direction, so the single slot goes to the next angle.
 */
const PREWARM_COUNT = 1;
const PREWARM_INTERVAL_MS = 10_000;

/**
 * Off-screen angles kept actually playing (see standbySlots). Each is one
 * more hidden decode; the TV and phones have the least to spare.
 */
const STANDBY_MAX: Record<typeof platform, number> = { desktop: 2, tv: 1, mobile: 1 };

/**
 * Playback health is judged, and reported to the proxy log, over windows of
 * this length. Long enough that one bad second does not decide anything.
 */
const PERF_INTERVAL_MS = 10_000;
/** Share of dropped frames in the big slots that counts as "not keeping up". */
const DROP_RATIO_LIMIT = 0.05;
/** Consecutive bad windows before 'auto' steps down one rung. */
const DROP_STRIKES = 2;
/** Windows to wait after a step, so the switch itself is not judged. */
const STEP_COOLDOWN = 3;
/** Fewer frames than this in a window is too little to judge. */
const MIN_FRAMES_TO_JUDGE = 100;

/** Build of the TV app, from its user agent; null in a browser. */
const appBuild = (() => {
  const m = /MultiviewTV\/(\d+)/.exec(navigator.userAgent);
  return m ? Number(m[1]) : null;
})();

/** Where the picture-in-picture window sits and how big it is. */
export interface PipPlacement {
  /** Horizontal position within the free space, 0 = left edge, 1 = right edge. */
  x: number;
  /** Vertical position within the free space, 0 = top, 1 = bottom. */
  y: number;
  size: PipSize;
  /** 1 = opaque; lower lets the main picture show through. */
  opacity: number;
}

/** Opacity steps of the floating window; below 0.4 it is hard to make out. */
export const PIP_OPACITIES = [1, 0.85, 0.7, 0.55, 0.4] as const;

/** Sizes of the floating window, with its width as a share of the screen. */
export const PIP_SIZES = [
  { value: 'xs', label: 'Sehr klein', width: 0.15 },
  { value: 's', label: 'Klein', width: 0.2 },
  { value: 'm', label: 'Mittel', width: 0.28 },
  { value: 'l', label: 'Groß', width: 0.36 },
  { value: 'xl', label: 'Sehr groß', width: 0.45 },
] as const;
export type PipSize = (typeof PIP_SIZES)[number]['value'];

const PIP_KEY = 'multiview.pip';
const SPLIT_KEY = 'multiview.split';

/** Share of the width the left column of a side-by-side layout gets. */
function loadSplit(): number {
  try {
    const raw = Number(localStorage.getItem(SPLIT_KEY));
    if (raw >= 0.2 && raw <= 0.8) return raw;
  } catch { /* storage unavailable */ }
  return 0.5;
}
const AUTO_KEY = 'multiview.auto';

/** The last automatic mode and period, so a new match starts the way the previous one ran. */
function loadAuto(): { mode: AutoMode; seconds: number } {
  try {
    const raw = JSON.parse(localStorage.getItem(AUTO_KEY) ?? 'null') as { mode?: string; seconds?: number } | null;
    if (raw) {
      const mode = (['off', 'interval', 'excitement', 'both'] as const).find((m) => m === raw.mode) ?? 'off';
      const seconds = typeof raw.seconds === 'number' ? clampInterval(raw.seconds) : 300;
      return { mode, seconds };
    }
  } catch { /* storage unavailable or garbage: use the default */ }
  return { mode: 'off', seconds: 300 };
}

function clampInterval(seconds: number): number {
  return Math.max(5, Math.min(3600, Math.round(seconds)));
}

const DEFAULT_PIP: PipPlacement = { x: 1, y: 1, size: 's', opacity: 1 };

function loadPip(): PipPlacement {
  try {
    const raw = JSON.parse(localStorage.getItem(PIP_KEY) ?? 'null') as Partial<PipPlacement> | null;
    if (raw && typeof raw.x === 'number' && typeof raw.y === 'number') {
      return {
        x: Math.min(1, Math.max(0, raw.x)),
        y: Math.min(1, Math.max(0, raw.y)),
        size: PIP_SIZES.find((s) => s.value === raw.size)?.value ?? 's',
        opacity: typeof raw.opacity === 'number' ? Math.min(1, Math.max(0.4, raw.opacity)) : 1,
      };
    }
  } catch { /* storage unavailable or garbage: use the default */ }
  return { ...DEFAULT_PIP };
}

/** How the main pane may change on its own. */
export type AutoMode = 'off' | 'interval' | 'excitement' | 'both';

export interface AutoEvent {
  angleId: string;
  label: string;
  reason: 'interval' | 'excitement' | 'goal';
  deltaDb?: number;
  /** Filled for goals: the score after it, and who scored. */
  detail?: string;
  at: number;
}

/** How long a goal may wait for its picture before it is given up on. */
const GOAL_HOLD_LIMIT_MS = 10 * 60 * 1000;
/** Seconds to switch early, so the build-up is visible and not just the finish. */
const GOAL_LEAD_SECONDS = 0;
/**
 * A cheer may not take the picture away sooner than this after the last switch.
 * Two angles of the same match hear the same crowd, and with only a per-track
 * cooldown they took turns firing -- the main pane flipped every 10-20 s.
 */
const EXCITEMENT_HOLD_MS = 45_000;

/**
 * The multiview session: which angles exist, which one is the main pane, where
 * the sound comes from, and how far each feed is shifted in time.
 *
 * Players are owned per angle rather than per slot, so cycling the main stream is
 * a CSS grid-area change plus a quality switch -- never a teardown. That is what
 * makes the arrow keys feel instant.
 */
export class MultiviewEngine {
  name = $state('');
  matchId = $state<number | null>(null);
  angles = $state<Angle[]>([]);
  layout = $state<LayoutId>('1+3');
  mainAngleId = $state<string | null>(null);
  audioAngleId = $state<string | null>(null);
  focusedAngleId = $state<string | null>(null);
  paused = $state(false);
  globalMute = $state(false);
  /** Per device, not per match: where the PiP window goes is about this screen. */
  pip = $state<PipPlacement>(loadPip());
  /** Left column's share in a resizable side-by-side layout, 0.2..0.8. */
  split = $state(loadSplit());

  /**
   * Concurrent streams the provider allows, or null when unlimited.
   *
   * This is a harder limit than the decoder budget: exceeding it does not make
   * playback stutter, it makes the provider cut streams mid-match.
   */
  upstreamBudget = $state<number | null>(null);

  #players = new Map<string, TilePlayer>();
  #states = $state<Record<string, { state: TileState; error: string | null }>>({});
  #stats = $state<Record<string, TileStats>>({});
  #downgradeTimer: number | null = null;
  #statsTimer: number | null = null;
  #prewarmTimer: number | null = null;
  #autoTimer: number | null = null;
  #detector: ExcitementDetector | null = null;
  #goalTimer: number | null = null;
  #lastEventStamp = 0;
  #stallTimes: number[] | null = null;
  #stallStrikes = 0;
  #audioDisableUnsubscribe: (() => void) | null = null;
  #perfTimer: number | null = null;
  #perfPrev = new Map<string, { epoch: number; dropped: number; total: number; at: number }>();
  #dropStrikes = 0;
  #perfCooldown = 0;
  #perfEvent: string | null = null;
  #mainChangedAt = 0;

  constructor() {
    setVariantHint(hintFor(this.maxQuality, this.qualityFloor));
    onMaxQualityChange(() => {
      this.maxQuality = getMaxQuality();
      // An explicit choice starts over; earlier automatic steps no longer apply.
      this.qualityFloor = 0;
      this.#dropStrikes = 0;
      this.#updateHint();
      this.#reconcileQualities();
    });
  }

  /* ----------------------------------------------------------- quality cap */

  /** This device's ceiling setting, from the settings page. */
  maxQuality = $state<MaxQuality>(getMaxQuality());
  /**
   * Automatic step-down for this viewing session: an index into the quality
   * ladder that 'auto' stays at or below. Reset with every new match.
   */
  qualityFloor = $state(0);

  /** Full-size slots currently on screen; 'auto' reduces when there are two. */
  readonly #bigSlotCount = $derived.by(() => {
    const full = LAYOUTS[this.effectiveLayout].fullQuality.slice(0, maxFullQuality) as string[];
    return this.slots.filter((s) => full.includes(s.slot) && s.angle.role !== 'audio').length;
  });

  /** The ceiling in force for full-size slots. */
  readonly ceiling = $derived<Ceiling>(effectiveCeiling(this.maxQuality, this.#bigSlotCount, this.qualityFloor));

  /* ------------------------------------------------------------ automatics */

  autoMode = $state<AutoMode>('off');
  /** Seconds between automatic switches in interval mode. */
  autoIntervalSeconds = $state(loadAuto().seconds);
  /** When interval mode switches next (epoch ms), null when it is not running. */
  autoNextAt = $state<number | null>(null);
  /** Most recent automatic switches, newest first. Shown as a short log. */
  autoLog = $state<AutoEvent[]>([]);
  /** Per-angle excursion above its own baseline, in dB. */
  excitement = $state<Record<string, number>>({});
  /** Set when the audio graph had to be abandoned; the tone features are then off. */
  audioDisabledReason = $state<string | null>(
    audioBus.disabledByPreference ? 'Auf diesem Gerät ausgeschaltet (Einstellungen → Wiedergabe)' : null,
  );

  /** Switch to an angle when its fixture's score changes. */
  goalSwitching = $state(false);
  /** Goals reported but not yet reached by the picture. */
  pendingGoals = $state<ScoreEventDto[]>([]);
  /** Last evaluation of the stall watchdog, for the technical display. */
  audioWatch = $state<{ onBus: number; strikes: number; moved: boolean } | null>(null);

  /** Angles that occupy a tile. A role of 'audio' contributes sound only. */
  readonly visibleAngles = $derived(this.angles.filter((a) => a.role !== 'audio'));

  readonly audioCandidates = $derived(this.angles.filter((a) => a.role === 'audio' || a.role === 'both'));

  /**
   * Layouts this setup can actually sustain. Three ceilings apply: how many
   * angles exist, how many tiles the hardware decodes, and how many streams the
   * provider allows at once -- the last being the one that bites hardest.
   */
  readonly availableLayouts = $derived.by(() => {
    const ceiling = Math.min(
      maxTiles,
      Math.max(1, this.visibleAngles.length),
      this.upstreamBudget ?? Number.POSITIVE_INFINITY,
    );
    return LAYOUT_CYCLE.filter((id) => LAYOUTS[id].slots <= ceiling);
  });

  readonly effectiveLayout = $derived<LayoutId>(
    this.availableLayouts.includes(this.layout)
      ? this.layout
      : (this.availableLayouts[this.availableLayouts.length - 1] ?? '1'),
  );

  /**
   * Slot assignment. The main angle takes 'm' and the rest fill a..h in order
   * starting after it, so cycling moves the mosaic predictably rather than
   * reshuffling every tile.
   */
  readonly slots = $derived.by<TileSlot[]>(() => {
    const visible = this.visibleAngles;
    if (visible.length === 0) return [];

    const def = LAYOUTS[this.effectiveLayout];
    const mainIndex = Math.max(0, visible.findIndex((a) => a.id === this.mainAngleId));
    const ordered = visible.map((_, i) => visible[(mainIndex + i) % visible.length]!);
    const names = ['m', 'a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];

    return ordered.slice(0, def.slots).map((angle, i) => ({
      angle,
      slot: names[i]!,
      state: this.#states[angle.id]?.state ?? 'idle',
      error: this.#states[angle.id]?.error ?? null,
      stats: this.#stats[angle.id] ?? null,
    }));
  });

  /**
   * Angles playing off-screen -- muted, at the tile rendition -- so that the
   * switch to them, by timer, goal or remote, only reveals a running picture
   * instead of a loading screen. A goal waiting for its angle comes first
   * (that angle also needs a player for its playback clock, or the goal never
   * becomes due), then whatever the cycle reaches next. Bounded by what the
   * device decodes and the provider allows beside what is on screen.
   */
  readonly standbySlots = $derived.by<TileSlot[]>(() => {
    const shown = new Set(this.slots.map((s) => s.angle.id));
    const audioOnly = this.angles.length - this.visibleAngles.length;
    const room = Math.min(
      STANDBY_MAX[platform],
      maxTiles - shown.size,
      (this.upstreamBudget ?? Number.POSITIVE_INFINITY) - shown.size - audioOnly,
    );
    if (room <= 0) return [];

    const visible = this.visibleAngles;
    const mainIndex = Math.max(0, visible.findIndex((a) => a.id === this.mainAngleId));
    const cycle = visible.map((_, i) => visible[(mainIndex + i) % visible.length]!).filter((a) => !shown.has(a.id));
    const goals = new Set(this.pendingGoals.map((g) => g.fixtureExternalId));
    const ordered = [
      ...cycle.filter((a) => a.fixtureExternalId && goals.has(a.fixtureExternalId)),
      ...cycle.filter((a) => !a.fixtureExternalId || !goals.has(a.fixtureExternalId)),
    ];
    return ordered.slice(0, room).map((angle) => ({
      angle,
      slot: 'standby',
      state: this.#states[angle.id]?.state ?? 'idle',
      error: this.#states[angle.id]?.error ?? null,
      stats: this.#stats[angle.id] ?? null,
    }));
  });

  readonly mainAngle = $derived(this.slots.find((s) => s.slot === 'm')?.angle ?? null);

  /* ----------------------------------------------------------------- session */

  loadMatch(match: Match): void {
    this.teardown();
    this.name = match.name;
    this.matchId = match.id;
    this.angles = match.angles;
    this.layout = match.layout;

    const firstVisible = match.angles.find((a) => a.role !== 'audio');
    this.mainAngleId = firstVisible?.id ?? match.angles[0]?.id ?? null;
    this.focusedAngleId = this.mainAngleId;

    // A dedicated commentary angle is the whole reason it exists, so it wins
    // audio focus over the main video feed.
    const dedicatedAudio = match.angles.find((a) => a.role === 'audio');
    this.audioAngleId = dedicatedAudio?.id ?? this.mainAngleId;

    // Linking a fixture has no other purpose than goal switching, so doing it
    // is taken as asking for it. The menu can still turn it off.
    if (match.angles.some((a) => a.fixtureExternalId)) this.setGoalSwitching(true);

    // Carry the automatic switching over from the previous match. The cheer
    // modes wait for the audio graph; without it only the timer remains.
    const auto = loadAuto();
    this.autoIntervalSeconds = auto.seconds;
    let mode = auto.mode;
    if (this.audioDisabledReason && mode !== 'off') mode = mode === 'excitement' ? 'off' : 'interval';
    this.#applyAutoMode(mode);
  }

  loadAdHoc(name: string, angles: Angle[], layout: LayoutId = '1+3'): void {
    this.loadMatch({ id: -1, name, kickoff: null, layout, angles, weekdays: [], time: null, createdAt: Date.now() });
    this.matchId = null;
  }

  /**
   * Adds an angle while watching -- "this game too" from the menu. A channel
   * already on screen is promoted instead of opened twice. A one-picture
   * layout grows to the smallest one that shows the newcomer.
   */
  addAngle(angle: Angle): void {
    const existing = this.angles.find((a) => a.channelId === angle.channelId);
    if (existing) {
      this.setMain(existing.id);
      return;
    }
    this.angles = [...this.angles, angle];
    if (this.mainAngleId === null) {
      this.mainAngleId = angle.id;
      this.focusedAngleId = angle.id;
      this.audioAngleId = angle.id;
    }
    const visible = this.visibleAngles.length;
    if (LAYOUTS[this.layout].slots < visible) {
      const fits = this.availableLayouts.find((id) => LAYOUTS[id].slots >= visible)
        ?? this.availableLayouts[this.availableLayouts.length - 1];
      if (fits) this.layout = fits;
    }
    if (angle.fixtureExternalId && !this.goalSwitching) this.setGoalSwitching(true);
  }

  /**
   * Puts an angle on another channel -- the match stays, the stream changes
   * (a slot showing the wrong game, a dead source). It gets a new id, so its
   * tile remounts with a fresh player, and keeps its place, role and roles as
   * main, focus and audio. A channel already on screen is promoted instead.
   */
  changeChannel(angleId: string, channel: { id: number; name: string }): void {
    const old = this.angles.find((a) => a.id === angleId);
    if (!old || old.channelId === channel.id) return;
    const existing = this.angles.find((a) => a.channelId === channel.id);
    if (existing) {
      this.setMain(existing.id);
      return;
    }
    const angle: Angle = {
      ...old,
      id: `draft-${channel.id}-${Date.now()}`,
      channelId: channel.id,
      // A bare channel is called after it; a match keeps its name.
      label: old.fixtureExternalId ? old.label : channel.name,
      // The old stream's delay says nothing about the new one.
      syncOffsetMs: 0,
    };
    this.unmount(angleId);
    this.angles = this.angles.map((a) => (a.id === angleId ? angle : a));
    if (this.mainAngleId === angleId) this.mainAngleId = angle.id;
    if (this.focusedAngleId === angleId) this.focusedAngleId = angle.id;
    if (this.audioAngleId === angleId) this.audioAngleId = angle.id;
    this.#applyMuting();
  }

  /** Takes an angle off the screen; the last one cannot be removed. */
  removeAngle(angleId: string): void {
    if (this.angles.length <= 1 || !this.angles.some((a) => a.id === angleId)) return;
    this.unmount(angleId);
    this.angles = this.angles.filter((a) => a.id !== angleId);
    const first = this.visibleAngles[0]?.id ?? this.angles[0]?.id ?? null;
    if (this.mainAngleId === angleId) this.mainAngleId = first;
    if (this.focusedAngleId === angleId) this.focusedAngleId = this.mainAngleId;
    if (this.audioAngleId === angleId) this.audioAngleId = this.mainAngleId;
    this.#applyMuting();
  }

  /** Attaches a container element to an angle and starts playback. */
  mount(angleId: string, container: HTMLElement): void {
    const angle = this.angles.find((a) => a.id === angleId);
    if (!angle || this.#players.has(angleId)) return;

    const quality = this.#desiredQuality(angle);
    const player = new TilePlayer(container, angle.channelId, quality);
    player.onStateChange = (state, detail) => {
      this.#states = { ...this.#states, [angleId]: { state, error: detail ?? null } };
    };
    player.setMuted(this.#shouldMute(angle));
    this.#players.set(angleId, player);

    this.#audioDisableUnsubscribe ??= audioBus.onDisabled((reason) => {
      this.audioDisabledReason = reason;
      this.excitement = {};
      // Excitement switching has nothing to listen to any more.
      if (this.autoMode === 'excitement') this.#applyAutoMode('off');
      else if (this.autoMode === 'both') this.#applyAutoMode('interval');
      for (const p of this.#players.values()) p.detachAudio();
    });

    void player.load().then(() => {
      player.applySyncOffset(angle.syncOffsetMs);
    }).catch(() => { /* surfaced through onStateChange */ });

    this.#ensureStatsTimer();
    this.#ensurePrewarmTimer();
    this.#ensurePerfTimer();
  }

  unmount(angleId: string): void {
    this.#players.get(angleId)?.destroy();
    this.#players.delete(angleId);
    const { [angleId]: _removedState, ...restStates } = this.#states;
    this.#states = restStates;
    const { [angleId]: _removedStats, ...restStats } = this.#stats;
    this.#stats = restStats;
  }

  teardown(): void {
    if (this.#downgradeTimer !== null) clearTimeout(this.#downgradeTimer);
    if (this.#statsTimer !== null) {
      clearInterval(this.#statsTimer);
      this.#statsTimer = null;
    }
    if (this.#prewarmTimer !== null) {
      clearInterval(this.#prewarmTimer);
      this.#prewarmTimer = null;
    }
    if (this.#perfTimer !== null) {
      clearInterval(this.#perfTimer);
      this.#perfTimer = null;
    }
    this.#perfPrev.clear();
    this.#dropStrikes = 0;
    this.#perfCooldown = 0;
    this.#perfEvent = null;
    // A new match is a new chance for the full ceiling.
    this.qualityFloor = 0;
    this.#updateHint();
    this.#stopAuto();
    if (this.#goalTimer !== null) {
      clearInterval(this.#goalTimer);
      this.#goalTimer = null;
    }
    this.goalSwitching = false;
    this.pendingGoals = [];
    this.#audioDisableUnsubscribe?.();
    this.#audioDisableUnsubscribe = null;
    this.#stallTimes = null;
    this.#stallStrikes = 0;
    this.autoLog = [];
    this.excitement = {};
    for (const player of this.#players.values()) player.destroy();
    this.#players.clear();
    this.#states = {};
    this.#stats = {};
    this.angles = [];
    this.mainAngleId = null;
    this.audioAngleId = null;
    this.focusedAngleId = null;
  }

  /* --------------------------------------------------------------- promoting */

  /**
   * The core interaction: step the main pane through the angles. Cheap by
   * design -- the newly-promoted angle is already decoding, so the swap is
   * immediate and only the quality upgrade happens asynchronously.
   */
  cycleMain(delta: 1 | -1): void {
    const visible = this.visibleAngles;
    if (visible.length < 2) return;
    const current = Math.max(0, visible.findIndex((a) => a.id === this.mainAngleId));
    const next = (current + delta + visible.length) % visible.length;
    this.setMain(visible[next]!.id);
  }

  selectMainByIndex(index: number): void {
    // Remote digits are 1-based; 0 is a convenient alias for the first angle.
    const visible = this.visibleAngles;
    const i = index === 0 ? 0 : index - 1;
    const angle = visible[i];
    if (angle) this.setMain(angle.id);
  }

  setMain(angleId: string): void {
    if (angleId === this.mainAngleId) return;
    const previousId = this.mainAngleId;
    this.mainAngleId = angleId;
    this.#mainChangedAt = Date.now();
    this.focusedAngleId = angleId;

    // Audio follows the main pane unless a dedicated commentary angle holds it.
    const audioAngle = this.angles.find((a) => a.id === this.audioAngleId);
    if (!audioAngle || audioAngle.role !== 'audio') this.audioAngleId = angleId;
    this.#applyMuting();
    if (previousId) this.#reconcileQualities();
    // Whatever made the switch -- remote, goal or cheer -- the new picture gets
    // its full period before the timer moves on.
    if (this.#autoTimer !== null) this.#scheduleInterval();
  }

  cycleLayout(delta: 1 | -1): void {
    const list = this.availableLayouts;
    if (list.length === 0) return;
    const current = list.indexOf(this.effectiveLayout);
    const next = (current + delta + list.length) % list.length;
    this.layout = list[next]!;
    this.#reconcileQualities();
  }

  setLayout(layout: LayoutId): void {
    this.layout = layout;
    this.#reconcileQualities();
  }

  /* --------------------------------------------------------------------- pip */

  setPip(placement: Partial<PipPlacement>): void {
    this.pip = { ...this.pip, ...placement };
    try {
      localStorage.setItem(PIP_KEY, JSON.stringify(this.pip));
    } catch { /* only a convenience */ }
  }

  /** Moves the side-by-side divider. `persist` is false while a drag is still running. */
  setSplit(value: number, persist = true): void {
    this.split = Math.min(0.8, Math.max(0.2, value));
    if (!persist) return;
    try {
      localStorage.setItem(SPLIT_KEY, String(this.split));
    } catch { /* only a convenience */ }
  }

  /* ----------------------------------------------------------------- quality */

  /**
   * Which rendition an angle should be playing. Big slots get the full one --
   * as far as this device's ceiling allows -- and small ones the downscaled
   * tile. The number of big ones is capped by what the device decodes, so a TV
   * in the four-way grid does not choke.
   */
  #desiredQuality(angle: Angle): Quality {
    const full = fullRendition(this.ceiling);
    if (angle.preferredQuality === 'main') return full;
    if (angle.preferredQuality === 'tile') return 'tile';
    // An audio-only angle never needs pixels.
    if (angle.role === 'audio') return 'tile';

    const slot = this.slots.find((s) => s.angle.id === angle.id)?.slot;
    if (!slot) return 'tile';
    const big = LAYOUTS[this.effectiveLayout].fullQuality.slice(0, maxFullQuality);
    return (big as string[]).includes(slot) ? full : 'tile';
  }

  /**
   * Brings every player to the rendition its slot calls for. Upgrades and
   * changes between full-size renditions start right away; downgrades to the
   * tile wait a moment, so a quick back-and-forth does not demote the angle the
   * user just returned to, and two switches do not fight over bandwidth during
   * a fast cycle.
   */
  #reconcileQualities(): void {
    let pendingDowngrade = false;
    for (const angle of this.angles) {
      const player = this.#players.get(angle.id);
      if (!player) continue;
      const desired = this.#desiredQuality(angle);
      if (desired !== 'tile') player.requestQuality(desired);
      else if (player.wantedQuality !== 'tile') pendingDowngrade = true;
    }
    if (!pendingDowngrade) return;

    if (this.#downgradeTimer !== null) clearTimeout(this.#downgradeTimer);
    this.#downgradeTimer = window.setTimeout(() => {
      this.#downgradeTimer = null;
      for (const angle of this.angles) {
        if (this.#desiredQuality(angle) === 'tile') this.#players.get(angle.id)?.requestQuality('tile');
      }
    }, DOWNGRADE_DELAY_MS);
  }

  #updateHint(): void {
    setVariantHint(hintFor(this.maxQuality, this.qualityFloor));
  }

  /* ------------------------------------------------------------------- audio */

  #shouldMute(angle: Angle): boolean {
    if (this.globalMute) return true;
    if (angle.role === 'video') return true;
    return angle.id !== this.audioAngleId;
  }

  #applyMuting(): void {
    for (const angle of this.angles) {
      this.#players.get(angle.id)?.setMuted(this.#shouldMute(angle));
    }
  }

  setAudioAngle(angleId: string): void {
    const angle = this.angles.find((a) => a.id === angleId);
    if (!angle || angle.role === 'video') return;
    this.audioAngleId = angleId;
    this.#applyMuting();
  }

  /** Green button: take sound from whatever tile is focused. */
  pinAudioToFocused(): void {
    if (this.focusedAngleId) this.setAudioAngle(this.focusedAngleId);
  }

  toggleMute(): void {
    this.globalMute = !this.globalMute;
    this.#applyMuting();
  }

  togglePause(): void {
    this.paused = !this.paused;
    for (const player of this.#players.values()) player.setPaused(this.paused);
  }

  /* -------------------------------------------------------------------- sync */

  /**
   * Nudges the focused angle's delay. Only a feed ahead of the reference can be
   * held back, hence the clamp at zero: if a tile is behind everything else,
   * every other tile has to be delayed instead.
   */
  nudgeSync(deltaMs: number): void {
    const id = this.focusedAngleId;
    if (!id) return;
    const index = this.angles.findIndex((a) => a.id === id);
    if (index < 0) return;

    const angle = this.angles[index]!;
    const next = Math.max(0, Math.min(60_000, angle.syncOffsetMs + deltaMs));
    if (next === angle.syncOffsetMs) return;

    const updated = [...this.angles];
    updated[index] = { ...angle, syncOffsetMs: next };
    this.angles = updated;
    this.#players.get(id)?.applySyncOffset(next);
  }

  setFocused(angleId: string): void {
    this.focusedAngleId = angleId;
  }

  /* ----------------------------------------------------------------- prewarm */

  /**
   * Angles outside the current layout have no player, so cycling to one would
   * otherwise pay the full cost: ffmpeg start, first segment, then buffering.
   * Resolving them on a timer keeps their proxy session alive and warm, which
   * removes the ffmpeg startup from that path -- only the client-side buffering
   * remains.
   *
   * Resolving also refreshes the session's idle timer, which is what stops the
   * reaper from collecting a stream that is about to be needed.
   */
  #ensurePrewarmTimer(): void {
    if (this.#prewarmTimer !== null) return;
    const run = () => { void this.#prewarm(); };
    this.#prewarmTimer = window.setInterval(run, PREWARM_INTERVAL_MS);
    run();
  }

  async #prewarm(): Promise<void> {
    // With a provider limit, warming an off-screen angle spends one of the few
    // connections available on something nobody is watching.
    if (this.upstreamBudget !== null) return;

    const visible = this.visibleAngles;
    // Standby angles play already; warming them would only repeat that.
    const shown = new Set([...this.slots, ...this.standbySlots].map((s) => s.angle.id));
    if (visible.length <= shown.size) return;

    const mainIndex = Math.max(0, visible.findIndex((a) => a.id === this.mainAngleId));
    const upcoming: typeof visible = [];

    for (let step = 1; step < visible.length && upcoming.length < PREWARM_COUNT; step++) {
      const candidate = visible[(mainIndex + step) % visible.length]!;
      if (!shown.has(candidate.id)) upcoming.push(candidate);
    }

    await Promise.all(
      // A failure here costs nothing: the angle simply loads cold when selected.
      upcoming.map((angle) => api.resolve(angle.channelId, 'tile', variantHint()).catch(() => undefined)),
    );
  }

  /* -------------------------------------------------------------- automatics */

  setAutoMode(mode: AutoMode): void {
    this.#applyAutoMode(mode);
    this.#saveAuto();
  }

  #applyAutoMode(mode: AutoMode): void {
    this.autoMode = mode;
    this.#stopAuto();
    if (mode === 'off') return;

    if (mode === 'interval' || mode === 'both') this.#scheduleInterval();

    if (mode === 'excitement' || mode === 'both') {
      this.#detector = new ExcitementDetector(
        () => this.#levels(),
        (event) => this.#onExcitement(event),
      );
      this.#detector.start();
    }
  }

  setAutoInterval(seconds: number): void {
    this.autoIntervalSeconds = clampInterval(seconds);
    this.#saveAuto();
    // Restart so the new period takes effect immediately rather than after the
    // old one elapses.
    if (this.autoMode === 'interval' || this.autoMode === 'both') this.#scheduleInterval();
  }

  /** The angle the timer would switch to next. */
  get nextAutoAngle(): Angle | null {
    return this.#nextAngleInCycle();
  }

  /** Switches to the next angle now; the timer then starts over. */
  autoSwitchNow(): void {
    const next = this.#nextAngleInCycle();
    if (next) this.#autoSwitch(next.id, next.label, 'interval');
  }

  /** (Re)starts the countdown to the next timed switch, measured from now. */
  #scheduleInterval(): void {
    if (this.#autoTimer !== null) clearTimeout(this.#autoTimer);
    const ms = this.autoIntervalSeconds * 1000;
    this.autoNextAt = Date.now() + ms;
    this.#autoTimer = window.setTimeout(() => {
      this.#autoTimer = null;
      const next = this.#nextAngleInCycle();
      // setMain reschedules; with nothing to switch to, just wait another round.
      if (next) this.#autoSwitch(next.id, next.label, 'interval');
      if (this.#autoTimer === null) this.#scheduleInterval();
    }, ms);
  }

  #saveAuto(): void {
    try {
      localStorage.setItem(AUTO_KEY, JSON.stringify({ mode: this.autoMode, seconds: this.autoIntervalSeconds }));
    } catch { /* only a convenience */ }
  }

  #stopAuto(): void {
    if (this.#autoTimer !== null) {
      clearTimeout(this.#autoTimer);
      this.#autoTimer = null;
    }
    this.autoNextAt = null;
    this.#detector?.stop();
    this.#detector = null;
  }

  #levels(): Map<string, number> {
    const out = new Map<string, number>();
    for (const angle of this.angles) {
      // A video-only angle carries no commentary worth listening to, and an
      // angle with no audio graph yet simply does not participate.
      if (angle.role === 'video') continue;
      const level = this.#players.get(angle.id)?.level();
      if (level !== null && level !== undefined) out.set(angle.id, level);
    }
    return out;
  }

  #nextAngleInCycle(): Angle | null {
    const visible = this.visibleAngles;
    if (visible.length < 2) return null;
    const current = Math.max(0, visible.findIndex((a) => a.id === this.mainAngleId));
    return visible[(current + 1) % visible.length] ?? null;
  }

  #onExcitement(event: ExcitementEvent): void {
    this.excitement = Object.fromEntries(this.#detector?.deltas() ?? []);

    const angle = this.angles.find((a) => a.id === event.id);
    if (!angle) return;

    // An audio-only angle is a commentary track, not something to show. Its
    // excitement still counts -- it just moves the sound, not the picture.
    if (angle.role === 'audio') {
      this.setAudioAngle(angle.id);
      this.#recordAuto({ angleId: angle.id, label: angle.label, reason: 'excitement', deltaDb: event.deltaDb, at: event.at });
      return;
    }

    if (angle.id === this.mainAngleId) return;
    if (Date.now() - this.#mainChangedAt < EXCITEMENT_HOLD_MS) return;
    this.#autoSwitch(angle.id, angle.label, 'excitement', event.deltaDb);
  }

  #autoSwitch(angleId: string, label: string, reason: AutoEvent['reason'], deltaDb?: number): void {
    this.setMain(angleId);
    this.#recordAuto({ angleId, label, reason, deltaDb, at: Date.now() });
  }

  #recordAuto(event: AutoEvent): void {
    this.autoLog = [event, ...this.autoLog].slice(0, 8);
  }

  /* --------------------------------------------------------------- goal feed */

  setGoalSwitching(enabled: boolean): void {
    this.goalSwitching = enabled;
    if (this.#goalTimer !== null) {
      clearInterval(this.#goalTimer);
      this.#goalTimer = null;
    }
    this.pendingGoals = [];
    if (!enabled) return;

    // Start from now, so goals scored before switching this on are not replayed.
    this.#lastEventStamp = Date.now();
    const fetchEvents = () => void this.#fetchGoals();
    this.#goalTimer = window.setInterval(fetchEvents, 10_000);
    fetchEvents();
  }

  async #fetchGoals(): Promise<void> {
    try {
      // Angles started ad hoc live only here; naming them is what gets the
      // proxy to watch their matches for goals at all.
      const watched = [...new Set(this.angles
        .filter((a) => a.fixtureProvider && a.fixtureExternalId)
        .map((a) => `${a.fixtureProvider}:${a.fixtureExternalId}`))];
      const { events } = await api.scoreEvents(this.#lastEventStamp, watched);
      if (events.length === 0) return;

      // Only goals belonging to a fixture one of the angles is showing.
      const linked = new Set(
        this.angles.map((a) => a.fixtureExternalId).filter((id): id is string => !!id),
      );
      const relevant = events.filter((e) => linked.has(e.fixtureExternalId));

      this.#lastEventStamp = Math.max(this.#lastEventStamp, ...events.map((e) => e.discoveredAt));
      if (relevant.length > 0) this.pendingGoals = [...this.pendingGoals, ...relevant];
    } catch {
      // A score feed hiccup must never disturb playback.
    }
  }

  /**
   * Releases goals whose moment has arrived on screen.
   *
   * The stream runs behind real time, so acting the instant the provider reports
   * a goal would switch to the camera before the goal happens -- and tell the
   * viewer what is coming. Each event therefore waits until the angle's own
   * playback clock, taken from the stream's wall-clock stamps, passes the moment
   * the goal was learned about.
   */
  #releaseDueGoals(): void {
    if (this.pendingGoals.length === 0) return;

    const now = Date.now();
    const keep: ScoreEventDto[] = [];

    for (const event of this.pendingGoals) {
      const angle = this.angles.find((a) => a.fixtureExternalId === event.fixtureExternalId);
      // An angle that is not playing has no clock of its own; the main
      // picture's comes from the same provider and runs about as far behind.
      const stats = angle
        ? this.#stats[angle.id] ?? (this.mainAngleId ? this.#stats[this.mainAngleId] : undefined)
        : undefined;

      if (!angle || !stats) {
        // Nothing showing it any more; drop once it is clearly stale.
        if (now - event.discoveredAt < GOAL_HOLD_LIMIT_MS) keep.push(event);
        continue;
      }

      // Without wall-clock stamps there is no safe moment, so act immediately
      // rather than hold forever -- being early beats never firing.
      const due = stats.wallClock === null
        ? true
        : stats.wallClock >= event.discoveredAt - GOAL_LEAD_SECONDS * 1000;

      if (!due) {
        if (now - event.discoveredAt < GOAL_HOLD_LIMIT_MS) keep.push(event);
        continue;
      }

      const score = `${event.homeScore}:${event.awayScore}`;
      const detail = event.scorer ? `${score} · ${event.scorer}` : score;
      if (angle.id !== this.mainAngleId && angle.role !== 'audio') {
        this.setMain(angle.id);
      }
      this.#recordAuto({ angleId: angle.id, label: angle.label, reason: 'goal', detail, at: now });
    }

    this.pendingGoals = keep;
  }

  /* ------------------------------------------------------------------- stats */

  /**
   * Watches for the one way the audio graph can fail badly: a connected element
   * has its clock driven by the graph, so if the graph stops advancing the
   * picture freezes too. Losing the level meters is a small price; losing
   * playback is not, so a stall drops the whole feature.
   */
  #watchForAudioStall(): void {
    if (audioBus.isDisabled()) return;

    // Only players actually routed through the graph can be frozen by it, and a
    // single frozen one is enough: judging by "all of them" would miss exactly
    // the case where some tiles still run on the plain path.
    const all = [...this.#players.values()];
    const onBus = all.filter((p) => p.usesAudioBus && p.shouldBeAdvancing);
    if (onBus.length === 0) {
      this.#stallTimes = null;
      this.#stallStrikes = 0;
      this.audioWatch = { onBus: 0, strikes: 0, moved: true };
      return;
    }

    const now = onBus.map((p) => p.currentTime);
    const before = this.#stallTimes;
    this.#stallTimes = now;

    if (!before || before.length !== now.length) {
      this.#stallStrikes = 0;
      return;
    }

    const anyStuck = now.some((t, i) => Math.abs(t - before[i]!) <= 0.05);
    this.#stallStrikes = anyStuck ? this.#stallStrikes + 1 : 0;
    this.audioWatch = { onBus: onBus.length, strikes: this.#stallStrikes, moved: !anyStuck };

    // Three quiet seconds while claiming to play is not a hiccup.
    if (this.#stallStrikes >= 3) {
      audioBus.disable('Wiedergabe stand still, während der Ton über den Audiograph lief');
    }
  }

  #ensureStatsTimer(): void {
    if (this.#statsTimer !== null) return;
    // Once a second is enough to spot dropped frames without becoming a cost
    // of its own on a weak TV box.
    this.#statsTimer = window.setInterval(() => {
      const next: Record<string, TileStats> = {};
      for (const [id, player] of this.#players) next[id] = player.stats();
      this.#stats = next;
      // The audio graph typically becomes available only after the first user
      // interaction, long after the tiles mounted.
      if (audioBus.isRunning() && !audioBus.isDisabled()) {
        for (const player of this.#players.values()) player.ensureAudio();
      }
      // Safety net: whatever sequence of switches came before, only the angle
      // holding audio focus is audible a second later at most.
      this.#applyMuting();
      if (this.#detector) this.excitement = Object.fromEntries(this.#detector.deltas());
      this.#releaseDueGoals();
      this.#watchForAudioStall();
    }, 1000);
  }

  /* ---------------------------------------------------- playback health */

  #ensurePerfTimer(): void {
    if (this.#perfTimer !== null) return;
    this.#perfTimer = window.setInterval(() => this.#perfTick(), PERF_INTERVAL_MS);
  }

  /**
   * Every ten seconds: frame counters per visible tile, turned into rates for
   * the window just past. Three things hang off that one measurement --
   *
   * - the proxy log gets a line per device (POST /api/telemetry), the only
   *   way to see what a TV box without developer access is actually doing;
   * - 'auto' steps the ceiling down a rung when the big pictures keep dropping
   *   frames, and stays there for the rest of the match;
   * - the TV app is asked to run the display at the content's frame rate.
   */
  #perfTick(): void {
    const now = performance.now();
    const tiles: TelemetryReport['tiles'] = [];
    let bigDrop = 0;
    let bigFrames = 0;
    let fallback = false;
    let mainFps: number | null = null;
    let seconds = PERF_INTERVAL_MS / 1000;

    // Standby angles are reported too (slot 'standby'): the only proof that
    // the next picture really runs ahead. They want the tile, so they never
    // count towards the drop judgement below.
    for (const slot of [...this.slots, ...this.standbySlots]) {
      const player = this.#players.get(slot.angle.id);
      if (!player) continue;
      player.enforceLevel();
      const s = player.stats();
      const prev = this.#perfPrev.get(slot.angle.id);
      this.#perfPrev.set(slot.angle.id, { epoch: s.epoch, dropped: s.droppedFrames, total: s.totalFrames, at: now });

      // Counters are per video element; across a reload or a switch they
      // restart and the difference means nothing.
      if (!prev || prev.epoch !== s.epoch || s.totalFrames < prev.total || player.state !== 'playing') continue;

      seconds = (now - prev.at) / 1000;
      const frames = s.totalFrames - prev.total;
      const drop = Math.max(0, s.droppedFrames - prev.dropped);
      const fps = seconds > 0 ? frames / seconds : 0;
      const wanted = player.wantedQuality;

      tiles.push({
        slot: slot.slot,
        q: wanted,
        r: s.rendition,
        w: s.width,
        h: s.height,
        fps: Math.round(fps * 10) / 10,
        drop,
        frames,
        buf: Math.round(s.bufferSeconds * 10) / 10,
        lat: Math.round(s.latencySeconds * 10) / 10,
        au: player.audioDiagnosis(),
      });

      if (wanted !== 'tile' && player.quality === wanted) {
        bigDrop += drop;
        bigFrames += frames;
        if (s.rendition !== null && s.rendition !== wanted) fallback = true;
      }
      if (slot.slot === 'm' && !this.paused) mainFps = fps;
    }

    this.#judgeDrops(bigDrop, bigFrames, fallback);
    if (mainFps !== null) matchFrameRate(mainFps);
    if (tiles.length === 0) return;

    const report: TelemetryReport = {
      platform,
      build: appBuild,
      display: displayDescription(),
      geo: this.#geometry(),
      layout: this.effectiveLayout,
      cap: this.maxQuality,
      effective: this.ceiling,
      step: this.qualityFloor,
      audioGraph: audioBus.isRunning(),
      seconds: Math.round(seconds),
      tiles,
    };
    if (this.#perfEvent) report.event = this.#perfEvent;
    this.#perfEvent = null;
    // Diagnostics only: a proxy that is busy or older must not disturb playback.
    void api.telemetry(report).catch(() => {});
  }

  /**
   * The automatic step-down. Only for 'auto' -- an explicit ceiling is the
   * user's call -- and only while variants actually arrive: if the proxy has
   * to serve 'main' instead, stepping further would change nothing.
   */
  #judgeDrops(drop: number, frames: number, fallback: boolean): void {
    if (this.maxQuality !== 'auto' || frames < MIN_FRAMES_TO_JUDGE) return;
    if (this.#perfCooldown > 0) {
      this.#perfCooldown--;
      return;
    }

    const ratio = drop / frames;
    if (ratio <= DROP_RATIO_LIMIT) {
      this.#dropStrikes = 0;
      return;
    }
    this.#dropStrikes++;
    if (this.#dropStrikes < DROP_STRIKES || fallback) return;

    const current = QUALITY_LADDER.indexOf(this.ceiling);
    if (current < 0 || current >= QUALITY_LADDER.length - 1) return;

    this.qualityFloor = current + 1;
    this.#dropStrikes = 0;
    this.#perfCooldown = STEP_COOLDOWN;
    this.#perfEvent = `herabgestuft ${QUALITY_LADDER[current]} -> ${this.ceiling} (Drops ${(ratio * 100).toFixed(1)} %)`;
    this.#updateHint();
    this.#reconcileQualities();
  }

  /** "window main-video" as WxH+X+Y in CSS px, e.g. 960x540 960x540+0+0. */
  #geometry(): string {
    const main = this.mainAngleId ? this.#players.get(this.mainAngleId) : undefined;
    const r = main?.activeVideo.getBoundingClientRect();
    const box = r ? `${Math.round(r.width)}x${Math.round(r.height)}+${Math.round(r.left)}+${Math.round(r.top)}` : '-';
    return `${window.innerWidth}x${window.innerHeight} ${box}`;
  }

  statsFor(angleId: string): TileStats | null {
    return this.#stats[angleId] ?? null;
  }

  /** Total dropped frames across tiles -- the number that says whether the
   *  current layout is actually sustainable on this hardware. */
  readonly totalDropped = $derived(
    Object.values(this.#stats).reduce((sum, s) => sum + s.droppedFrames, 0),
  );
}

export const engine = new MultiviewEngine();
